import { useState, useCallback } from 'react';
import { LeadQuery } from '@troia-v3/types';
import { LeadsAdvancedFilters as AdvancedFiltersType } from '../types';

/**
 * Filtros padrão aplicados pelo backend
 * Refletindo o comportamento default da API
 * Exportado para uso em componentes
 */
export const DEFAULT_FILTERS: AdvancedFiltersType = {
  businessStatus: ['pending']
};

/**
 * Hook para gerenciar filtros de leads
 *
 * Gerencia estado de filtros avançados e operadores selecionados,
 * convertendo para o formato esperado pela API (LeadQuery).
 *
 * Comportamento:
 * - Filtros locais (advancedFilters) são modificados internamente
 * - Apenas ao clicar "Aplicar" que appliedFilters é atualizado
 * - appliedFilters é usado para construir a query da API
 *
 * @example
 * ```tsx
 * const {
 *   advancedFilters,
 *   appliedFilters,
 *   selectedOperatorIds,
 *   handleAdvancedFiltersChange,
 *   handleOperatorSelectionChange,
 *   handleApplyFilters,
 *   toLeadQuery
 * } = useLeadsFilters();
 *
 * const query = toLeadQuery();
 * const { data } = useLeads(query);
 * ```
 */
export const useLeadsFilters = () => {
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersType>(DEFAULT_FILTERS);
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<AdvancedFiltersType>(DEFAULT_FILTERS);

  /**
   * Atualiza filtros avançados (estado local, não aplicado ainda)
   */
  const handleAdvancedFiltersChange = useCallback((filters: AdvancedFiltersType) => {
    setAdvancedFilters(filters);
  }, []);

  /**
   * Atualiza seleção de operadores
   */
  const handleOperatorSelectionChange = useCallback((operatorIds: string[]) => {
    setSelectedOperatorIds(operatorIds);
  }, []);

  /**
   * Aplica filtros avançados
   * @param filtersToApply - Filtros a aplicar (se não fornecidos, usa advancedFilters do state)
   */
  const handleApplyFilters = useCallback((filtersToApply?: AdvancedFiltersType) => {
    setAppliedFilters(filtersToApply || advancedFilters);
  }, [advancedFilters]);

  /**
   * Converte filtros para o formato LeadQuery esperado pela API
   */
  const toLeadQuery = useCallback((baseQuery?: Partial<LeadQuery>): LeadQuery => {
    // 🔍 DEBUG: Log dos filtros aplicados
    console.log('🔍 [toLeadQuery] appliedFilters:', appliedFilters);
    console.log('🔍 [toLeadQuery] selectedOperatorIds:', selectedOperatorIds);
    console.log('🔍 [toLeadQuery] baseQuery:', baseQuery);

    const filters: LeadQuery['filters'] = {};

    // Operadores
    if (selectedOperatorIds.length > 0) {
      // API espera um único assigneeId, então pegamos o primeiro
      // TODO: Decidir comportamento - filtrar por múltiplos operadores ou apenas um?
      filters.assigneeId = selectedOperatorIds[0];
    }

    // Priority
    if (appliedFilters.priority) {
      filters.priority = appliedFilters.priority;
      console.log('✅ [toLeadQuery] Adicionou priority:', appliedFilters.priority);
    }

    // Temperature
    if (appliedFilters.temperature) {
      filters.temperature = appliedFilters.temperature;
      console.log('✅ [toLeadQuery] Adicionou temperature:', appliedFilters.temperature);
    }

    // Qualify Status
    if (appliedFilters.qualifyStatus) {
      filters.qualifyStatus = appliedFilters.qualifyStatus;
      console.log('✅ [toLeadQuery] Adicionou qualifyStatus:', appliedFilters.qualifyStatus);
    }

    // Business Status
    if (appliedFilters.businessStatus) {
      filters.businessStatus = appliedFilters.businessStatus;
      console.log('✅ [toLeadQuery] Adicionou businessStatus:', appliedFilters.businessStatus);
    }

    // Segment
    if (appliedFilters.segment) {
      filters.segment = appliedFilters.segment;
      console.log('✅ [toLeadQuery] Adicionou segment:', appliedFilters.segment);
    }

    // Source
    if (appliedFilters.source) {
      filters.source = appliedFilters.source;
      console.log('✅ [toLeadQuery] Adicionou source:', appliedFilters.source);
    }

    // Medium
    if (appliedFilters.medium) {
      filters.medium = appliedFilters.medium;
      console.log('✅ [toLeadQuery] Adicionou medium:', appliedFilters.medium);
    }

    // Channel
    if (appliedFilters.channel) {
      filters.channel = appliedFilters.channel;
      console.log('✅ [toLeadQuery] Adicionou channel:', appliedFilters.channel);
    }

    // Origin
    if (appliedFilters.origin) {
      filters.origin = appliedFilters.origin;
      console.log('✅ [toLeadQuery] Adicionou origin:', appliedFilters.origin);
    }

    // Funil / Etapa
    if (appliedFilters.funnelId) {
      filters.funnelId = appliedFilters.funnelId;
    }
    if (appliedFilters.stepId) {
      filters.stepId = appliedFilters.stepId;
    }

    // Type
    if (appliedFilters.type) {
      filters.type = appliedFilters.type;
      console.log('✅ [toLeadQuery] Adicionou type:', appliedFilters.type);
    }

    // Campaign Name
    if (appliedFilters.campaignName) {
      filters.campaignName = appliedFilters.campaignName;
      console.log('✅ [toLeadQuery] Adicionou campaignName:', appliedFilters.campaignName);
    }

    // Adset Name
    if (appliedFilters.adsetName) {
      filters.adsetName = appliedFilters.adsetName;
      console.log('✅ [toLeadQuery] Adicionou adsetName:', appliedFilters.adsetName);
    }

    // Ad Name
    if (appliedFilters.adName) {
      filters.adName = appliedFilters.adName;
      console.log('✅ [toLeadQuery] Adicionou adName:', appliedFilters.adName);
    }

    // Tags
    if (appliedFilters.tags) {
      filters.tags = appliedFilters.tags;
      console.log('✅ [toLeadQuery] Adicionou tags:', appliedFilters.tags);
    }

    // Date From
    if (appliedFilters.dateFrom) {
      filters.dateFrom = appliedFilters.dateFrom;
      console.log('✅ [toLeadQuery] Adicionou dateFrom:', appliedFilters.dateFrom);
    }

    // Date To
    if (appliedFilters.dateTo) {
      filters.dateTo = appliedFilters.dateTo;
      console.log('✅ [toLeadQuery] Adicionou dateTo:', appliedFilters.dateTo);
    }

    // Data do ganho / Data da perda (ranges sobre wonDate/lostDate)
    if (appliedFilters.wonDateFrom) filters.wonDateFrom = appliedFilters.wonDateFrom;
    if (appliedFilters.wonDateTo) filters.wonDateTo = appliedFilters.wonDateTo;
    if (appliedFilters.lostDateFrom) filters.lostDateFrom = appliedFilters.lostDateFrom;
    if (appliedFilters.lostDateTo) filters.lostDateTo = appliedFilters.lostDateTo;

    // Formulário (D3) — só entra quando há ao menos uma pergunta com valor
    if (appliedFilters.formAnswers && appliedFilters.formAnswers.length > 0) {
      filters.formAnswers = appliedFilters.formAnswers;
    }
    // Formulário escolhido no filtro (qualquer um, não só o de captura)
    if (appliedFilters.formAnswersFormId) {
      filters.formAnswersFormId = appliedFilters.formAnswersFormId;
    }

    // Merge com filtros do baseQuery (ex: funnelId)
    const mergedFilters = {
      ...baseQuery?.filters,
      ...filters
    };

    console.log('🔍 [toLeadQuery] FINAL mergedFilters:', mergedFilters);

    // Só retorna filters se houver ao menos um filtro aplicado
    const hasFilters = Object.keys(mergedFilters).length > 0;

    return {
      ...baseQuery,
      filters: hasFilters ? mergedFilters : undefined
    };
  }, [selectedOperatorIds, appliedFilters]);

  /**
   * Conta quantos filtros estão ativos
   */
  const activeFiltersCount = Object.values(appliedFilters).filter(v =>
    v !== undefined && v !== '' && (Array.isArray(v) ? v.length > 0 : true)
  ).length + (selectedOperatorIds.length > 0 ? 1 : 0);

  return {
    advancedFilters,
    selectedOperatorIds,
    appliedFilters,
    activeFiltersCount,
    handleAdvancedFiltersChange,
    handleOperatorSelectionChange,
    handleApplyFilters,
    toLeadQuery
  };
};
